<template>
  <div class="d-flex flex-nowrap overflow-auto crisisTableContainer">
    <div class="p-1 w-100 crisis-report-panel">
      <div class="d-flex">
        <div class="d-flex">
          <h5>{{ $t('operational_security.crisis_form.allocations_by_facility') }} -
            <span class="font-italic">{{ data.name }}-{{ data.reference_number }}</span>
          </h5>
        </div>
        <feather-icon @click="showFiltersFn" icon="FilterIcon" size="16" class="ml-1 cursor-pointer"/>
      </div>

      <div class="crisis-report-search">
        <b-form-input
          v-model="searchQuery"
          placeholder="Buscar por SIRH o nombre de sucursal"
        />
      </div>

      <div class="crisis-report-filters" v-if="showFilters">
        <h5>{{ $t('operational_security.crisis_form.filters_by_region_city') }}</h5>
        <div class="d-flex flex-wrap">
          <div v-for="(region, index) in selected" :key="region.region" class="crisis-report-filter-group">
            <b-form-checkbox
              :name="`option-filter-region${index}`"
              v-model="region.checked"
              :disabled="region.isDisabled"
              @change="onRegionFilterChange(region, $event)"
            >
              {{ region.region }}
            </b-form-checkbox>

            <div class="crisis-report-city-list" v-if="!region.checked">
              <b-form-checkbox
                v-for="city in region.cities"
                :key="city.key"
                :name="`option-filter-city${index}`"
                v-model="city.checked"
                :disabled="city.isDisabled"
              >
                {{ city.name }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <h5>{{ $t("custom_forms.categories") }}</h5>
          <div class="metric-grid ml-1">
            <div
              v-for="(category, index) in categoryFilters"
              :key="category.key"
              class="metric-item"
            >
              <b-form-checkbox
                :name="`option-filter-category${index}`"
                v-model="category.checked"
                :disabled="category.isDisabled"
              >
                {{ category.name }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <b-form-checkbox
            name="Only rows with data"
            v-model="showOnlyRowsWithData"
          >
            {{ $t('operational_security.crisis_form.only_rows_with_data') }}
          </b-form-checkbox>
        </div>
      </div>

      <SkeletonLoading
        v-if="isMasterDataLoading"
        :count="1"
        :height="230"
        :fullWidth="true"
      />

      <div class="crisis-report-grid-shell">
        <crisis-report-grid
          v-if="!isMasterDataLoading && !dataNotExists"
          :source="gridRows"
          :columns="gridColumns"
          height="100%"
        />

        <div class="w-100 h-100 d-flex justify-content-center align-items-center"
             v-if="dataNotExists">
          <p class="text-center">No existe información para esta crisis</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import {computed, getCurrentInstance, onMounted, ref, watch} from '@vue/composition-api';
import axios from "@/libs/axios";
import {useCrisisFormUpdate} from "./composables/useCrisisFormUpdateSocket";
import CrisisReportGrid from './CrisisReportGrid.vue';
import {
  hasAnyRegionOrCitySelection,
  makeColumnGroup,
  makeGridColumn,
  onRegionSelectionChange
} from './composables/useCrisisReportGrid';

export default {
  components: {
    CrisisReportGrid,
    SkeletonLoading
  },
  props: {
    data: {
      type: Object,
    },
    slotId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy;
    const {lastUpdate} = useCrisisFormUpdate(props.data.id);
    const tableData = ref([]);
    const searchQuery = ref('');
    const isMasterDataLoading = ref(false);
    const dataNotExists = ref(false);
    const showFilters = ref(false);
    const selected = ref([]);
    const categoryFilters = ref([]);
    const showOnlyRowsWithData = ref(false);

    const baseColumnKeys = ['sirh', 'nombre_sucursal', 'municipio', 'entidad'];
    const filterableCategoryKeys = [
      'impact_on_operations',
      'communication',
      'water_leaks',
      'broken_glass',
      'atm_damages',
      'risk_facilities',
      'blocked_roads'
    ];

    const tableColumns = [
      {key: 'sirh', label: 'custom_forms.sirh'},
      {key: 'nombre_sucursal', label: 'operational_security.prefill.facility'},
      {key: 'municipio', label: 'custom_forms.municipality'},
      {key: 'entidad', label: 'custom_forms.state'},
      {
        key: 'impact_on_operations',
        label: 'operational_security.crisis_form.inpact_on_operations',
        children: [
          {
            key: 'total_closure',
            label: 'operational_security.crisis_form.complete_closure'
          },
          {key: 'early_closing', label: 'operational_security.crisis_form.early_closing'},
          {key: 'late_opening', label: 'operational_security.crisis_form.late_opening'}
        ]
      },
      {
        key: 'communication',
        label: 'operational_security.crisis_form.out_of_service',
        children: [
          {key: 'offline', label: 'operational_security.crisis_form.offline'},
          {key: 'restore', label: 'operational_security.crisis_form.reset'},
          {key: 'cctv', label: 'operational_security.crisis_form.CCTV'},
        ]
      },
      {
        key: 'water_leaks',
        label: 'operational_security.crisis_form.water_leakage',
        children: [
          {
            key: 'patio_publico',
            label: 'operational_security.crisis_form.public_courtyard'
          },
          {
            key: 'areas_internas',
            label: 'operational_security.crisis_form.internal_areas'
          },
          {key: 'recinto_de_cajeros', label: 'operational_security.crisis_form.atm_area'},
          {
            key: 'caja_transfer_etv',
            label: 'operational_security.crisis_form.etv_transfer_box'
          },
          {key: 'tren_cajas', label: 'operational_security.crisis_form.box_train'},
          {key: 'estacionamiento', label: 'operational_security.crisis_form.parking'},
        ]
      },
      {
        key: 'broken_glass',
        label: 'operational_security.crisis_form.broken_glass',
        children: [
          {
            key: 'laterales_fachada',
            label: 'operational_security.crisis_form.side_walls_or_facade'
          },
          {
            key: 'acceso_principal',
            label: 'operational_security.crisis_form.main_entrance'
          },
          {key: 'recinto_de_cajeros', label: 'operational_security.crisis_form.atm_area'},
          {
            key: 'patio_publico',
            label: 'operational_security.crisis_form.public_courtyard'
          },
        ]
      },
      {
        key: 'atm_damages',
        label: 'operational_security.crisis_form.atm_damages',
        children: [
          {
            key: 'rotura_pantalla',
            label: 'operational_security.crisis_form.broken_screen'
          },
          {key: 'chapa_daniada', label: 'operational_security.crisis_form.broken_lock'},
          {key: 'abierto', label: 'operational_security.crisis_form.open'},
        ]
      },
      {
        key: 'risk_facilities',
        label: 'operational_security.crisis_form.risk_facilities',
        children: [
          {
            key: 'has_operational_backup_power',
            label: 'operational_security.crisis_form.has_operational_backup_power'
          },
          {
            key: 'backup_power_capacity',
            label: 'operational_security.crisis_form.backup_power_capacity'
          },
          {key: 'diesel_level', label: 'operational_security.crisis_form.diesel_level'},
        ]
      },
      {key: 'blocked_roads', label: 'operational_security.crisis_form.blocked_roads'},
    ];

    const enabledCategoryKeys = computed(() => categoryFilters.value
      .filter(category => category.checked)
      .map(category => category.key));

    const visibleTableColumns = computed(() => {
      if (!enabledCategoryKeys.value.length) return tableColumns;

      return tableColumns.filter(column =>
        baseColumnKeys.includes(column.key) ||
        enabledCategoryKeys.value.includes(column.key)
      );
    });

    const gridColumns = computed(() => visibleTableColumns.value.map(column => {
      if (!column.children) {
        return makeGridColumn({
          prop: column.key,
          name: vm.$t(column.label)
        });
      }

      return makeColumnGroup(
        vm.$t(column.label),
        column.children.map(child => makeGridColumn({
          prop: `${column.key}__${child.key}`,
          name: vm.$t(child.label)
        }))
      );
    }));

    const normalizedSearchQuery = computed(() => normalizeSearchValue(searchQuery.value));

    const filteredTableData = computed(() => {
      let rows = tableData.value;

      if (normalizedSearchQuery.value) {
        rows = rows.filter(row => [
          row.sirh,
          row.nombre_sucursal,
          row.description
        ].some(value => normalizeSearchValue(value).includes(normalizedSearchQuery.value)));
      }

      if (hasAnyRegionOrCitySelection(selected.value)) {
        rows = rows.filter(row => isRowInSelectedLocation(row));
      }

      if (showOnlyRowsWithData.value) {
        rows = rows.filter(row => rowHasDataForVisibleCategories(row));
      }

      return rows;
    });

    const gridRows = computed(() => filteredTableData.value.map(row => {
      const gridRow = {};

      visibleTableColumns.value.forEach(column => {
        if (!column.children) {
          gridRow[column.key] = row[column.key];
          return;
        }

        column.children.forEach(child => {
          gridRow[`${column.key}__${child.key}`] = row[column.key]?.[child.key] ?? '';
        });
      });

      return gridRow;
    }));

    onMounted(() => {
      getAllocationByFacilities();
    });

    watch(lastUpdate, () => getAllocationByFacilities());

    async function getAllocationByFacilities() {
      try {
        isMasterDataLoading.value = true;
        dataNotExists.value = false;
        const {data} = await axios.get(`/v1/operational-security-crisis/${props.data.id}/building-information`);
        tableData.value = transformData(data.data || []);
        setFilterOptions();
        setCategoryFilters();
      } catch (error) {
        dataNotExists.value = true;
      } finally {
        isMasterDataLoading.value = false;
      }
    }

    const transformData = data => data.map(item => ({
      sirh: item?.sirh,
      nombre_sucursal: item?.nombre_sucursal,
      description: item?.description,
      municipio: item?.municipio,
      entidad: item?.entidad,
      impact_on_operations: {
        total_closure: item?.impact_on_operations?.total_closure ?? "",
        early_closing: item?.impact_on_operations?.early_closing ?? "",
        late_opening: item?.impact_on_operations?.late_opening ?? ""
      },
      communication: {
        offline: item?.communication?.offline ?? "",
        restore: item?.communication?.restore ?? "",
        cctv: item?.communication?.cctv ?? ""
      },
      water_leaks: {
        patio_publico: item?.water_leaks?.patio_publico ?? "",
        areas_internas: item?.water_leaks?.areas_internas ?? "",
        recinto_de_cajeros: item?.water_leaks?.recinto_de_cajeros ?? "",
        caja_transfer_etv: item?.water_leaks?.caja_transfer_etv ?? "",
        tren_cajas: item?.water_leaks?.tren_cajas ?? "",
        estacionamiento: item?.water_leaks?.estacionamiento ?? ""
      },
      broken_glass: {
        laterales_fachada: item?.broken_glass?.laterales_fachada ?? "",
        recinto_de_cajeros: item?.broken_glass?.recinto_de_cajeros ?? "",
        acceso_principal: item?.broken_glass?.acceso_principal ?? "",
        patio_publico: item?.broken_glass?.patio_publico ?? "",
      },
      atm_damages: {
        rotura_pantalla: item?.atm_damages?.rotura_pantalla,
        chapa_daniada: item?.atm_damages?.chapa_daniada,
        abierto: item?.atm_damages?.abierto,
      },
      risk_facilities: {
        has_operational_backup_power: item?.risk_facilities?.has_operational_backup_power ?? "",
        backup_power_capacity: item?.risk_facilities?.backup_power_capacity ?? "",
        diesel_level: item?.risk_facilities?.diesel_level ?? "",
      },
      operational_atms: item?.operational_atms ?? 0,
      blocked_roads: item?.blocked_roads ?? 0,
    }));

    const normalizeSearchValue = value => String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    function setFilterOptions() {
      const regionMap = {};

      tableData.value.forEach(row => {
        const region = row.entidad || '';
        const municipality = row.municipio || '';

        if (!region) return;

        if (!regionMap[region]) {
          regionMap[region] = new Set();
        }

        if (municipality) {
          regionMap[region].add(municipality);
        }
      });

      selected.value = Object.entries(regionMap).map(([region, municipalities]) => ({
        region,
        checked: false,
        isDisabled: false,
        cities: [...municipalities].map(municipality => ({
          key: municipality,
          name: municipality,
          checked: false,
          isDisabled: false
        }))
      }));
    }

    function setCategoryFilters() {
      categoryFilters.value = tableColumns
        .filter(column => filterableCategoryKeys.includes(column.key))
        .map(column => ({
          key: column.key,
          name: vm.$t(column.label),
          checked: false,
          isDisabled: false
        }));
    }

    function isRowInSelectedLocation(row) {
      const selectedRegion = selected.value.find(region => region.region === row.entidad);
      if (!selectedRegion) return false;
      if (selectedRegion.checked) return true;

      return selectedRegion.cities.some(city =>
        city.key === row.municipio &&
        city.checked
      );
    }

    function rowHasDataForVisibleCategories(row) {
      const columns = visibleTableColumns.value.filter(column =>
        filterableCategoryKeys.includes(column.key)
      );

      return columns.some(column => {
        if (!column.children) return hasUsefulValue(getColumnValue(row, column));

        return column.children.some(child =>
          hasUsefulValue(getColumnValue(row, column, child))
        );
      });
    }

    function getColumnValue(row, column, child = null) {
      if (!child) return row[column.key];

      return row[column.key]?.[child.key];
    }

    function hasUsefulValue(value) {
      if (value === null || value === undefined) return false;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') {
        const normalizedValue = value.trim();
        if (!normalizedValue) return false;

        return Number(normalizedValue) !== 0 || Number.isNaN(Number(normalizedValue));
      }

      return Boolean(value);
    }

    function showFiltersFn() {
      showFilters.value = !showFilters.value;
    }

    function onRegionFilterChange(region, checked) {
      onRegionSelectionChange(region, checked);
    }

    return {
      gridColumns,
      gridRows,
      searchQuery,
      isMasterDataLoading,
      dataNotExists,
      selected,
      showFilters,
      categoryFilters,
      showOnlyRowsWithData,
      showFiltersFn,
      onRegionFilterChange
    };
  }
};
</script>

<style scoped lang="scss">
.crisisTableContainer {
  height: 100%;
  min-height: 0;
  background-color: #fff;
}

.crisis-report-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.crisis-report-grid-shell {
  flex: 1 1 auto;
  min-height: 420px;
  height: calc(100vh - 240px);
}

.crisis-report-search {
  margin: 12px 0 16px;
}

.crisis-report-filters {
  margin: 12px 0 16px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.crisis-report-filter-group {
  margin: 0 18px 14px 0;
}

.crisis-report-city-list {
  margin: 8px 0 0 16px;
}

.crisis-report-filter-section {
  margin-top: 14px;
}

.metric-grid {
  max-width: 650px;
  max-height: 150px;
  display: flex;
  flex-wrap: wrap;
}

.metric-item {
  width: 100px;
}
</style>
